import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2, Download, FileText, Printer, Search, Calendar, Filter as FilterIcon,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet,
  Eye, DollarSign, Users, Percent, Landmark, Info, X, CheckCircle2, Clock,
  Award,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Master Agent Commissions — professional dashboard for MASTER_AGENT.
 *
 * Sections:
 *   1. Header + description
 *   2. Five colored KPI summary cards (Current Period, Total Sales, Total
 *      Agents, Total Commissions, Next Payment).
 *   3. Info banner explaining auto-payout.
 *   4. Toolbar: Date-range picker with presets, Search, Filter, Print,
 *      Export CSV/XLSX/PDF.
 *   5. Sortable per-agent table with View Details.
 *   6. Footer totals row.
 *   7. Detail modal per agent (transaction/weekly/payment breakdown).
 */

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function fmtUsd(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDateRange(from, to) {
  if (!from || !to) return "—";
  const fmt = (s) => {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  };
  return `${fmt(from)} — ${fmt(to)}`;
}

function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function presetRange(key) {
  const now = new Date();
  const today = localDate(now);
  const back = (days) => {
    const d = new Date(now);
    d.setDate(d.getDate() - days);
    return localDate(d);
  };
  const monthsBack = (m) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - m);
    return localDate(d);
  };
  switch (key) {
    case "today": return [today, today];
    case "yesterday": return [back(1), back(1)];
    case "7d": return [back(6), today];
    case "30d": return [back(29), today];
    case "this_month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return [localDate(s), today];
    }
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return [localDate(s), localDate(e)];
    }
    case "3m": return [monthsBack(3), today];
    case "6m": return [monthsBack(6), today];
    case "12m": return [monthsBack(12), today];
    default: return [today, today];
  }
}

export default function MasterAgentCommissions() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "MASTER_ADMIN";

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateInitialised, setDateInitialised] = useState(false);
  const [masters, setMasters] = useState([]);
  const [masterId, setMasterId] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [onlyWithSales, setOnlyWithSales] = useState(false);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [nextPayoutAt, setNextPayoutAt] = useState(null);
  const [detailAgent, setDetailAgent] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master-commissions/by-agent", {
        params: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          master_agent_id: isAdmin && masterId ? masterId : undefined,
          search: search || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
          page,
          page_size: pageSize,
        },
      });
      setData(r.data);
      if (!dateInitialised && r.data?.date_from && r.data?.date_to) {
        setDateFrom(r.data.date_from);
        setDateTo(r.data.date_to);
        setDateInitialised(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, masterId, isAdmin, search, sortBy, sortDir, page, pageSize, dateInitialised]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => {
    if (isAdmin) {
      api.get("/master-agents").then((r) => setMasters(r.data || [])).catch(() => {});
    }
    // Backend-authoritative next payout timestamp (respects platform tz).
    api.get("/master-commissions/summary")
      .then((r) => setNextPayoutAt(r.data?.next_payout_at || null))
      .catch(() => {});
  }, [isAdmin]);

  const items = data?.items;
  const filteredItems = useMemo(() => {
    const arr = items || [];
    return onlyWithSales ? arr.filter((r) => (r.txn_total || 0) > 0) : arr;
  }, [items, onlyWithSales]);
  const totals = data?.totals || {};
  const pagRaw = data?.pagination;
  const pag = useMemo(() => (
    pagRaw || { page: 1, page_size: pageSize, total_pages: 1, total_items: 0 }
  ), [pagRaw, pageSize]);

  const nextPaymentDate = useMemo(() => (
    nextPayoutAt ? new Date(nextPayoutAt) : null
  ), [nextPayoutAt]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(1);
  };

  const doExport = async (kind) => {
    try {
      const r = await api.get(`/master-commissions/by-agent/export.${kind}`, {
        params: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          master_agent_id: isAdmin && masterId ? masterId : undefined,
          search: search || undefined,
        },
        responseType: "blob",
      });
      const mime = kind === "csv" ? "text/csv"
        : kind === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `master-agent-commissions-${dateFrom}_to_${dateTo}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const downloadCertificate = async (userId, filenameHint) => {
    try {
      const r = await api.get(`/master-commissions/certificate/${userId}`, {
        params: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commission-certificate-${filenameHint}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Certificate download failed");
    }
  };

  const pages = useMemo(() => {
    const total = pag.total_pages;
    const current = pag.page;
    const set = new Set([1, total, current, current - 1, current + 1]);
    return [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  }, [pag]);

  return (
    <div className="space-y-6" data-testid="master-agent-commissions-page">
      {/* Header */}
      <div>
        <h1
          className="text-2xl sm:text-3xl font-bold text-[#0F62FE]"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("mac.title", "Master Agent Commissions")}
        </h1>
        <p className="text-slate-500 mt-1 text-sm max-w-3xl">
          {t("mac.subtitle_v2", "View your agents' sales, your accumulated commissions, and your next automatic weekly payment.")}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          testid="kpi-period"
          label="SELECTED PERIOD"
          value={fmtDateRange(data?.date_from || dateFrom, data?.date_to || dateTo)}
          sub={
            data?.date_from && data?.date_to && data.date_from === data.date_to
              ? "Single day"
              : "Filtered range"
          }
          icon={<Calendar className="w-6 h-6" />}
          tone="blue"
        />
        <KpiCard
          testid="kpi-sales"
          label="TOTAL SALES"
          value={fmtUsd(totals.total_sales_usd)}
          sub="USD"
          icon={<DollarSign className="w-6 h-6" />}
          tone="emerald"
        />
        <KpiCard
          testid="kpi-agents"
          label="TOTAL AGENTS"
          value={totals.total_agents ?? 0}
          sub="Subagents"
          icon={<Users className="w-6 h-6" />}
          tone="amber"
        />
        <KpiCard
          testid="kpi-commissions"
          label="TOTAL COMMISSIONS"
          value={fmtUsd(totals.master_agent_commissions_usd)}
          sub="USD"
          icon={<Percent className="w-6 h-6" />}
          tone="violet"
        />
        <KpiCard
          testid="kpi-next-payment"
          label="NEXT PAYMENT"
          value={nextPaymentDate ? nextPaymentDate.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—"}
          sub={nextPaymentDate ? "Monday 02:00 AM" : "—"}
          icon={<Landmark className="w-6 h-6" />}
          tone="teal"
          badge={nextPaymentDate ? "SCHEDULED" : null}
        />
      </div>

      {/* Info banner */}
      <div
        data-testid="mac-info-banner"
        className="flex items-start gap-3 rounded-lg border border-[#0F62FE]/30 bg-[#EDF5FF] px-4 py-3 text-sm text-slate-700"
      >
        <Info className="w-5 h-5 text-[#0F62FE] mt-0.5 flex-shrink-0" />
        <div>
          Master Agent commissions accumulate automatically during the week and are paid every
          Monday at <span className="font-semibold">02:00 AM</span> to your main wallet.
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 print:hidden">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between">
          <div className="flex flex-col md:flex-row gap-3 md:items-end flex-1">
            <div className="flex-1 min-w-[260px]">
              <label className="block text-xs text-slate-500 uppercase tracking-widest mb-1.5">
                Date range
              </label>
              <DateRangePopover
                from={dateFrom}
                to={dateTo}
                onChange={(f, tt) => { setDateFrom(f); setDateTo(tt); setPage(1); }}
              />
            </div>
            {isAdmin && (
              <div className="min-w-[200px]">
                <label className="block text-xs text-slate-500 uppercase tracking-widest mb-1.5">
                  Master Agent
                </label>
                <select
                  data-testid="mac-master-filter"
                  value={masterId}
                  onChange={(e) => { setMasterId(e.target.value); setPage(1); }}
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                >
                  <option value="">All master agents</option>
                  {masters.map((m) => (
                    <option key={m.id} value={m.id}>{m.name || m.username}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-widest mb-1.5">
                Search
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="mac-search"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search agents…"
                  className="h-11 w-56 rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                />
              </div>
            </div>
            <FilterButton onlyWithSales={onlyWithSales} setOnlyWithSales={setOnlyWithSales} />
            <button
              data-testid="mac-print"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border border-slate-300 bg-white text-sm font-medium hover:bg-slate-50"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
            <button
              data-testid="mac-export-pdf"
              onClick={() => doExport("pdf")}
              className="inline-flex items-center gap-1.5 h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50"
            >
              <FileText className="w-4 h-4" /> PDF
            </button>
            <button
              data-testid="mac-export-xlsx"
              onClick={() => doExport("xlsx")}
              className="inline-flex items-center gap-1.5 h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50"
            >
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </button>
            <button
              data-testid="mac-export-csv"
              onClick={() => doExport("csv")}
              className="inline-flex items-center gap-1.5 h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            {user?.role === "MASTER_AGENT" && (
              <button
                data-testid="mac-my-certificate"
                onClick={() => downloadCertificate(user.id, `${user.username}-${dateFrom}_to_${dateTo}`)}
                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              >
                <Award className="w-4 h-4" /> My Certificate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-600 print:hidden">
          <span>Show</span>
          <select
            data-testid="mac-page-size"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (<option key={n} value={n}>{n}</option>))}
          </select>
          <span>entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mac-agents-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <Th col="id" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>ID</Th>
                <Th col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>NAME</Th>
                <Th col="txn_total" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>TXN TOTAL</Th>
                <Th col="total_sales" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>TOTAL SALES (USD)</Th>
                <Th col="agent_commission" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>AGENT COMMISSION (USD)</Th>
                <Th col="master_commission" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>MASTER AGENT COMMISSION (USD)</Th>
                <th className="px-6 py-3 text-right text-[11px] font-bold uppercase tracking-widest text-slate-500 w-36">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t("common.loading")}
                </td></tr>
              ) : filteredItems.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-slate-500">
                  No agents match your filters for this period.
                </td></tr>
              ) : filteredItems.map((r) => (
                <tr
                  key={r.agent_id}
                  data-testid={`mac-agent-row-${r.agent_code}`}
                  className="border-b border-slate-100 hover:bg-slate-50/60"
                >
                  <td className="px-6 py-3.5 text-slate-600 font-medium">{r.agent_code || "—"}</td>
                  <td className="px-6 py-3.5 text-slate-900">{r.agent_name || r.agent_username || "—"}</td>
                  <td className="px-6 py-3.5 text-slate-900">{r.txn_total}</td>
                  <td className="px-6 py-3.5 text-slate-900">{fmtUsd(r.total_sales_usd)}</td>
                  <td className="px-6 py-3.5 text-slate-900">{fmtUsd(r.agent_commission_usd)}</td>
                  <td className="px-6 py-3.5 font-semibold text-slate-900">{fmtUsd(r.master_agent_commission_usd)}</td>
                  <td className="px-6 py-3.5 text-right">
                    <button
                      data-testid={`mac-view-details-${r.agent_code}`}
                      onClick={() => setDetailAgent(r)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-[#0F62FE] text-[#0F62FE] bg-white text-xs font-medium hover:bg-[#0F62FE]/5"
                    >
                      <Eye className="w-3.5 h-3.5" /> View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/50 print:hidden">
          <div className="text-xs text-slate-500">
            {pag.total_items > 0
              ? `Showing ${((pag.page - 1) * pag.page_size) + 1} to ${Math.min(pag.page * pag.page_size, pag.total_items)} of ${pag.total_items} entries`
              : "No entries"}
          </div>
          <div className="flex items-center gap-1" data-testid="mac-pagination">
            <button
              data-testid="mac-page-prev"
              disabled={pag.page <= 1}
              onClick={() => setPage(pag.page - 1)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {pages.map((n, i) => {
              const prev = pages[i - 1];
              const gap = prev && n - prev > 1;
              return (
                <React.Fragment key={n}>
                  {gap && <span className="w-6 text-center text-slate-400">…</span>}
                  <button
                    data-testid={`mac-page-${n}`}
                    onClick={() => setPage(n)}
                    className={
                      "inline-flex items-center justify-center min-w-[36px] h-9 px-2 rounded-md border text-sm font-medium " +
                      (n === pag.page
                        ? "bg-[#0F62FE] border-[#0F62FE] text-white"
                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100")
                    }
                  >
                    {n}
                  </button>
                </React.Fragment>
              );
            })}
            <button
              data-testid="mac-page-next"
              disabled={pag.page >= pag.total_pages}
              onClick={() => setPage(pag.page + 1)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer totals */}
      <div
        data-testid="mac-totals"
        className="bg-white rounded-lg border border-slate-200 shadow-sm px-6 py-4 grid grid-cols-2 md:grid-cols-5 gap-6"
      >
        <TotalCell label="TXN TOTAL" value={totals.txn_total ?? 0} testid="tot-txn" />
        <TotalCell label="TOTAL AGENTS" value={totals.total_agents ?? 0} testid="tot-subagents" />
        <TotalCell label="TOTAL SALES (USD)" value={fmtUsd(totals.total_sales_usd)} testid="tot-sales" />
        <TotalCell label="AGENT COMMISSIONS (USD)" value={fmtUsd(totals.agent_commissions_usd)} testid="tot-agent-comm" />
        <TotalCell label="MASTER AGENT COMMISSIONS (USD)" value={fmtUsd(totals.master_agent_commissions_usd)} testid="tot-master-comm" strong />
      </div>

      {detailAgent && (
        <AgentDetailModal
          row={detailAgent}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setDetailAgent(null)}
          onDownloadCertificate={() => downloadCertificate(
            detailAgent.agent_id,
            `${detailAgent.agent_code || detailAgent.agent_username}-${dateFrom}_to_${dateTo}`
          )}
        />
      )}
    </div>
  );
}

function fmtDateRangeShort(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  const s = new Date(startIso);
  const e = new Date(endIso);
  // The end is exclusive next-Monday, so display "up to Sunday" (end - 1 day).
  const eDisp = new Date(e);
  eDisp.setDate(eDisp.getDate() - 1);
  const opts = { month: "short", day: "2-digit" };
  const y = s.getFullYear();
  return `${s.toLocaleDateString("en-US", opts)} – ${eDisp.toLocaleDateString("en-US", opts)}, ${y}`;
}

function KpiCard({ testid, label, value, sub, icon, tone, badge }) {
  const bg = {
    blue: "bg-[#0F62FE]",
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    violet: "bg-violet-500",
    teal: "bg-teal-500",
  }[tone];
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
      <div className={`w-12 h-12 rounded-full ${bg} text-white flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-lg font-bold text-slate-900 mt-1 truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {value}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
          {sub}
          {badge && (
            <span className="inline-flex px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold text-[9px] uppercase tracking-wider">
              {badge}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Th({ children, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-widest text-slate-600 select-none cursor-pointer whitespace-nowrap"
      onClick={() => onSort(col)}
      data-testid={`mac-th-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <Icon className={"w-3 h-3 " + (active ? "text-[#0F62FE]" : "text-slate-400")} />
      </span>
    </th>
  );
}

function TotalCell({ label, value, testid, strong = false }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 " + (strong ? "text-lg font-bold text-slate-900" : "text-lg font-semibold text-slate-800")}>
        {value}
      </div>
    </div>
  );
}

function FilterButton({ onlyWithSales, setOnlyWithSales }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        data-testid="mac-filter-btn"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-medium hover:bg-[#0050E6]"
      >
        <FilterIcon className="w-4 h-4" /> Filter
      </button>
      {open && (
        <div className="absolute z-50 right-0 mt-1 w-64 bg-white rounded-lg border border-slate-200 shadow-lg p-4" data-testid="mac-filter-panel">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Filters</div>
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-700">
            <input
              data-testid="mac-filter-only-sales"
              type="checkbox"
              checked={onlyWithSales}
              onChange={(e) => setOnlyWithSales(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-[#0F62FE] focus:ring-[#0F62FE]"
            />
            Only show agents with sales
          </label>
        </div>
      )}
    </div>
  );
}

function DateRangePopover({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(from);
  const [tmpTo, setTmpTo] = useState(to);
  const ref = useRef(null);
  useEffect(() => { setTmpFrom(from); setTmpTo(to); }, [from, to]);
  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const applyPreset = (key) => {
    const [f, tt] = presetRange(key);
    setTmpFrom(f); setTmpTo(tt);
  };
  const apply = () => {
    if (!tmpFrom || !tmpTo) return;
    onChange(tmpFrom, tmpTo);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="mac-date-range-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50"
      >
        <Calendar className="w-4 h-4 text-slate-500" />
        <span className="flex-1 text-left">{from && to ? `${from} — ${to}` : "Select date range"}</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-[420px] bg-white rounded-lg border border-slate-200 shadow-lg p-4" data-testid="mac-date-range-popover">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Presets</div>
              <PresetBtn testid="preset-today" onClick={() => applyPreset("today")}>Today</PresetBtn>
              <PresetBtn testid="preset-yesterday" onClick={() => applyPreset("yesterday")}>Yesterday</PresetBtn>
              <PresetBtn testid="preset-7d" onClick={() => applyPreset("7d")}>Last 7 Days</PresetBtn>
              <PresetBtn testid="preset-30d" onClick={() => applyPreset("30d")}>Last 30 Days</PresetBtn>
              <PresetBtn testid="preset-this-month" onClick={() => applyPreset("this_month")}>This Month</PresetBtn>
              <PresetBtn testid="preset-last-month" onClick={() => applyPreset("last_month")}>Last Month</PresetBtn>
              <PresetBtn testid="preset-3m" onClick={() => applyPreset("3m")}>Last 3 Months</PresetBtn>
              <PresetBtn testid="preset-6m" onClick={() => applyPreset("6m")}>Last 6 Months</PresetBtn>
              <PresetBtn testid="preset-12m" onClick={() => applyPreset("12m")}>Last 12 Months</PresetBtn>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Custom range</div>
              <label className="block text-xs text-slate-500 mb-1">From</label>
              <input
                data-testid="mac-date-from"
                type="date"
                value={tmpFrom || ""}
                onChange={(e) => setTmpFrom(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
              <label className="block text-xs text-slate-500 mb-1">To</label>
              <input
                data-testid="mac-date-to"
                type="date"
                value={tmpTo || ""}
                onChange={(e) => setTmpTo(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              data-testid="mac-date-cancel"
              onClick={() => setOpen(false)}
              className="h-9 px-4 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              data-testid="mac-date-apply"
              onClick={apply}
              disabled={!tmpFrom || !tmpTo}
              className="h-9 px-4 rounded-md bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PresetBtn({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="block w-full text-left h-8 px-3 rounded text-sm text-slate-700 hover:bg-[#EDF5FF] hover:text-[#0F62FE]"
    >
      {children}
    </button>
  );
}

function AgentDetailModal({ row, dateFrom, dateTo, onClose, onDownloadCertificate }) {
  const [tab, setTab] = useState("weekly");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/master-commissions/agent-detail/${row.agent_id}`, {
      params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
    })
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Failed to load details"))
      .finally(() => setLoading(false));
  }, [row.agent_id, dateFrom, dateTo]);

  return (
    <div
      data-testid="mac-detail-modal"
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500">Agent details</div>
            <div className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {row.agent_name}
              <span className="ml-2 text-sm font-normal text-slate-500">@{row.agent_username} · ID {row.agent_code}</span>
            </div>
            {detail && (
              <div className="text-xs text-slate-500 mt-1">
                Period: {detail.date_from} → {detail.date_to}
              </div>
            )}
          </div>
          <button data-testid="mac-detail-close" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        {onDownloadCertificate && (
          <div className="px-6 py-3 flex items-center justify-end gap-2 border-b border-slate-100 bg-slate-50/60">
            <button
              data-testid="mac-detail-certificate"
              onClick={onDownloadCertificate}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
            >
              <Award className="w-4 h-4" /> Download Commission Certificate (PDF)
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin inline" /> Loading…
          </div>
        ) : !detail ? (
          <div className="py-16 text-center text-slate-500">Failed to load.</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4 p-6 bg-slate-50 border-b border-slate-200">
              <MiniStat label="TXN Total" value={detail.totals.txn_total} testid="det-txn" />
              <MiniStat label="Total Sales (USD)" value={fmtUsd(detail.totals.total_sales_usd)} testid="det-sales" />
              <MiniStat label="Agent Commission" value={fmtUsd(detail.totals.agent_commission_usd)} testid="det-agent-comm" />
              <MiniStat label="Master Commission" value={fmtUsd(detail.totals.master_agent_commission_usd)} testid="det-master-comm" strong />
            </div>

            <div className="border-b border-slate-200 flex gap-1 px-6">
              <TabBtn active={tab === "weekly"} onClick={() => setTab("weekly")} testid="tab-weekly">Weekly totals</TabBtn>
              <TabBtn active={tab === "transactions"} onClick={() => setTab("transactions")} testid="tab-tx">Transactions</TabBtn>
              <TabBtn active={tab === "sales"} onClick={() => setTab("sales")} testid="tab-sales">Sales</TabBtn>
              <TabBtn active={tab === "commissions"} onClick={() => setTab("commissions")} testid="tab-commissions">Commission history</TabBtn>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {tab === "weekly" && <WeeklyTable weekly={detail.weekly} />}
              {tab === "transactions" && <TxTable rows={detail.transactions} />}
              {tab === "sales" && <SalesTable rows={detail.transactions} />}
              {tab === "commissions" && <CommissionsTable rows={detail.transactions} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={"px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors " + (active ? "border-[#0F62FE] text-[#0F62FE]" : "border-transparent text-slate-500 hover:text-slate-900")}
    >
      {children}
    </button>
  );
}

function MiniStat({ label, value, testid, strong = false }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 " + (strong ? "text-xl font-bold text-slate-900" : "text-lg font-semibold text-slate-800")}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = {
    PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
    PENDING: "bg-amber-100 text-amber-700 border-amber-200",
    REVERSED: "bg-rose-100 text-rose-700 border-rose-200",
  }[status] || "bg-slate-100 text-slate-700 border-slate-200";
  const Icon = status === "PAID" ? CheckCircle2 : Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-widest border ${cls}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

function WeeklyTable({ weekly }) {
  if (!weekly?.length) return <Empty msg="No weekly activity." />;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm" data-testid="det-weekly-table">
        <thead>
          <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <th className="px-3 py-2 text-left">Week</th>
            <th className="px-3 py-2 text-right">TXN</th>
            <th className="px-3 py-2 text-right">Sales</th>
            <th className="px-3 py-2 text-right">Agent %</th>
            <th className="px-3 py-2 text-right">Master %</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {weekly.map((w) => (
            <tr key={w.week_start} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800">
                {(w.label_from || w.week_start.slice(0, 10))} → {(w.label_to || w.week_end.slice(0, 10))}
              </td>
              <td className="px-3 py-2 text-right">{w.txn_total}</td>
              <td className="px-3 py-2 text-right">{fmtUsd(w.total_sales_usd)}</td>
              <td className="px-3 py-2 text-right">{fmtUsd(w.agent_commission_usd)}</td>
              <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtUsd(w.master_agent_commission_usd)}</td>
              <td className="px-3 py-2"><StatusBadge status={w.payment_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TxTable({ rows }) {
  if (!rows?.length) return <Empty msg="No transactions." />;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm" data-testid="det-tx-table">
        <thead>
          <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Operator</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-right">Agent %</th>
            <th className="px-3 py-2 text-right">Master %</th>
            <th className="px-3 py-2 text-right">Accrual</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
              <td className="px-3 py-1.5 text-slate-800">{a.operator_name || a.operator_id}</td>
              <td className="px-3 py-1.5 text-right">{fmtUsd(a.topup_amount_usd)}</td>
              <td className="px-3 py-1.5 text-right">{Number(a.agent_pct).toFixed(2)}%</td>
              <td className="px-3 py-1.5 text-right">{Number(a.margin_pct).toFixed(2)}%</td>
              <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">{fmtUsd(a.accrual_amount_usd)}</td>
              <td className="px-3 py-1.5"><StatusBadge status={a.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SalesTable({ rows }) {
  if (!rows?.length) return <Empty msg="No sales." />;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm" data-testid="det-sales-table">
        <thead>
          <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Operator</th>
            <th className="px-3 py-2 text-right">Sale Amount (USD)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
              <td className="px-3 py-1.5 text-slate-800">{a.operator_name || a.operator_id}</td>
              <td className="px-3 py-1.5 text-right font-medium">{fmtUsd(a.topup_amount_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommissionsTable({ rows }) {
  if (!rows?.length) return <Empty msg="No commissions." />;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm" data-testid="det-commissions-table">
        <thead>
          <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-right">Admin %</th>
            <th className="px-3 py-2 text-right">Agent %</th>
            <th className="px-3 py-2 text-right">Master %</th>
            <th className="px-3 py-2 text-right">Sale</th>
            <th className="px-3 py-2 text-right">Master commission</th>
            <th className="px-3 py-2 text-left">Payment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
              <td className="px-3 py-1.5 text-right">{Number(a.admin_pct).toFixed(2)}%</td>
              <td className="px-3 py-1.5 text-right">{Number(a.agent_pct).toFixed(2)}%</td>
              <td className="px-3 py-1.5 text-right">{Number(a.margin_pct).toFixed(2)}%</td>
              <td className="px-3 py-1.5 text-right">{fmtUsd(a.topup_amount_usd)}</td>
              <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">{fmtUsd(a.accrual_amount_usd)}</td>
              <td className="px-3 py-1.5"><StatusBadge status={a.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="py-10 text-center text-sm text-slate-500">{msg}</div>;
}
